import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';

const ConfidentialitePage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="confidentialite-page">
      <SEO 
        title="Politique de Confidentialité | Atlantique Spa Boutique"
        description="Politique de confidentialité et protection des données personnelles sur le site Atlantique Spa Boutique."
        canonical="/confidentialite"
      />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Politique de Confidentialité</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Politique de Confidentialité</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-8">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              Atlantique Spa Boutique s'engage à protéger la vie privée des utilisateurs de son site internet. 
              Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos données personnelles 
              conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Responsable du traitement</h2>
            <p className="text-slate-600 leading-relaxed">
              Le responsable du traitement des données personnelles est :<br /><br />
              <strong>Atlantique Spa Boutique</strong><br />
              Email : contact@atlantiquespaboutique.com<br />
              Email : contact@atlantiquespaboutique.com
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Données collectées</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Nous collectons les données suivantes :
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Données d'identification : nom, prénom, adresse email, numéro de téléphone</li>
              <li>Données de livraison : adresse postale</li>
              <li>Données de commande : historique des commandes, produits achetés</li>
              <li>Données de navigation : pages visitées, durée de visite (via cookies analytiques)</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Finalités du traitement</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Vos données personnelles sont collectées et traitées pour :
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Le traitement et le suivi de vos commandes</li>
              <li>La livraison des produits</li>
              <li>La gestion de la relation client et du service après-vente</li>
              <li>L'envoi d'informations relatives à vos commandes</li>
              <li>L'amélioration de notre site et de nos services</li>
              <li>Le respect de nos obligations légales</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Base légale du traitement</h2>
            <p className="text-slate-600 leading-relaxed">
              Le traitement de vos données repose sur :<br />
              - L'exécution d'un contrat (traitement de commande)<br />
              - Notre intérêt légitime (amélioration de nos services)<br />
              - Votre consentement (cookies analytiques, newsletter si applicable)<br />
              - Nos obligations légales (conservation des factures)
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Durée de conservation</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données personnelles sont conservées pendant la durée nécessaire aux finalités pour lesquelles 
              elles ont été collectées :<br /><br />
              - Données clients : 3 ans après la dernière commande<br />
              - Données de facturation : 10 ans (obligation légale)<br />
              - Données de navigation : 13 mois maximum
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Destinataires des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données peuvent être transmises à :<br />
              - Nos prestataires de livraison<br />
              - Nos prestataires de paiement sécurisé<br />
              - Notre hébergeur<br /><br />
              Ces prestataires n'utilisent vos données que pour exécuter les services qui leur sont confiés 
              et sont soumis aux mêmes obligations de confidentialité.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Vos droits</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
              <li><strong>Droit à la limitation</strong> : limiter le traitement de vos données</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Pour exercer ces droits, contactez-nous à : contact@atlantiquespaboutique.com
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Cookies</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Notre site utilise des cookies pour :
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Cookies essentiels</strong> : nécessaires au fonctionnement du site (panier, session)</li>
              <li><strong>Cookies analytiques</strong> : mesure d'audience pour améliorer le site (avec votre consentement)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Vous pouvez configurer votre navigateur pour refuser les cookies ou être averti lorsqu'un cookie est déposé.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Sécurité</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données 
              contre tout accès non autorisé, modification, divulgation ou destruction.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Réclamation</h2>
            <p className="text-slate-600 leading-relaxed">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation 
              auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) : www.cnil.fr
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question relative à cette politique de confidentialité :<br />
              Email : contact@atlantiquespaboutique.com<br />
              Email : contact@atlantiquespaboutique.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialitePage;
